// frontend/src/pages/admin/CVGeneration/EmployeeListPanel.js
import React, { useState, useMemo, useEffect } from 'react';
import { CheckSquare, Square, AlertCircle, CheckCircle, XCircle, Users, ChevronDown, ChevronRight, MinusSquare } from 'lucide-react';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { CountBadge } from '../../../components/UI/CountBadge';

/**
 * Quality indicator component showing data completeness status.
 */
const QualityIndicator = ({ score, missingCount }) => {
  if (score >= 80) {
    return (
      <span className="flex items-center gap-1 text-green-600" title="Complete data">
        <CheckCircle className="w-3.5 h-3.5" />
      </span>
    );
  } else if (score >= 50) {
    return (
      <span className="flex items-center gap-1 text-amber-500" title={`${missingCount} fields missing`}>
        <AlertCircle className="w-3.5 h-3.5" />
        <span className="text-xs">{missingCount}</span>
      </span>
    );
  } else {
    return (
      <span className="flex items-center gap-1 text-red-500" title="Incomplete data">
        <XCircle className="w-3.5 h-3.5" />
      </span>
    );
  }
};

/**
 * Department Section Header - Collapsible with select all
 */
const DepartmentHeader = ({ 
  department, 
  employeeCount, 
  selectedCount,
  isExpanded, 
  onToggleExpand, 
  onSelectAll 
}) => {
  const allSelected = selectedCount === employeeCount && employeeCount > 0;
  const someSelected = selectedCount > 0 && selectedCount < employeeCount;

  return (
    <div 
      className="flex items-center gap-2.5 px-6 py-2.5 bg-gray-100 border-b border-gray-200 border-l-2 border-l-transparent cursor-pointer hover:bg-gray-200 sticky top-0 z-10 transition-colors"
      onClick={onToggleExpand}
    >
      {/* Expand/Collapse Icon */}
      {isExpanded ? (
        <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
      ) : (
        <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
      )}
      
      {/* Checkbox for select all in department */}
      <div 
        className="checkbox-area flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onSelectAll();
        }}
      >
        {allSelected ? (
          <CheckSquare className="w-4 h-4 text-red-600" />
        ) : someSelected ? (
          <MinusSquare className="w-4 h-4 text-red-600" />
        ) : (
          <Square className="w-4 h-4 text-gray-400 hover:text-gray-600" />
        )}
      </div>
      
      {/* Department Name & Count */}
      <span className="flex-1 font-medium text-sm text-gray-700 truncate">
        {department}
      </span>
      <CountBadge
        count={selectedCount > 0 ? selectedCount : employeeCount}
        theme="admin"
        size="md"
        max={9999}
        className="flex-shrink-0"
      />
    </div>
  );
};

/**
 * Employee List Panel for CV Generation.
 * 
 * Features:
 * - Search/filter employees
 * - **Grouped by department** (collapsible sections)
 * - Multi-select with checkboxes
 * - Data quality indicators
 * - Click to preview (separate from selection)
 */
const EmployeeListPanel = ({
  employees = [],
  selectedEmployees = [],
  previewEmployeeId = null,
  onSelectEmployee,
  onToggleSelectAll,
  onPreviewEmployee,
  isLoading = false,
  qualityData = {},
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [collapsedDepartments, setCollapsedDepartments] = useState(new Set());

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter employees by debounced search term
  const filteredEmployees = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return employees;
    const search = debouncedSearchTerm.toLowerCase();
    return employees.filter(emp =>
      emp.full_name?.toLowerCase().includes(search) ||
      emp.staff_id?.toLowerCase().includes(search) ||
      (emp.designation || '').toLowerCase().includes(search) ||
      (emp.department || '').toLowerCase().includes(search)
    );
  }, [employees, debouncedSearchTerm]);

  // Group employees by department
  const groupedByDepartment = useMemo(() => {
    const groups = {};
    filteredEmployees.forEach(emp => {
      const dept = emp.department || 'Unassigned';
      if (!groups[dept]) {
        groups[dept] = [];
      }
      groups[dept].push(emp);
    });
    
    // Sort departments alphabetically, but put "Unassigned" at the end
    const sortedDepts = Object.keys(groups).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    
    return { groups, sortedDepts };
  }, [filteredEmployees]);

  // Calculate total selected and all selected status
  const totalSelected = selectedEmployees.length;
  const allSelected = filteredEmployees.length > 0 &&
    filteredEmployees.every(emp => selectedEmployees.includes(emp.staff_id));

  // Get quality info for an employee
  const getQualityInfo = (staffId) => {
    const empQuality = qualityData.employees?.find(e => e.staff_id === staffId);
    return empQuality || { quality_score: 0, missing_count: 0 };
  };

  const isSelected = (staffId) => selectedEmployees.includes(staffId);
  const isPreviewing = (staffId) => previewEmployeeId === staffId;

  const handleRowClick = (employee, e) => {
    // If clicking on checkbox area, toggle selection
    if (e.target.closest('.checkbox-area')) {
      onSelectEmployee(employee.staff_id);
    } else {
      // Otherwise, preview the employee
      onPreviewEmployee(employee.staff_id);
    }
  };

  const handleSelectAllClick = () => {
    const staffIds = filteredEmployees.map(emp => emp.staff_id);
    onToggleSelectAll(staffIds, !allSelected);
  };

  const handleDepartmentSelectAll = (department) => {
    const deptEmployees = groupedByDepartment.groups[department] || [];
    const deptStaffIds = deptEmployees.map(emp => emp.staff_id);
    const allDeptSelected = deptEmployees.every(emp => selectedEmployees.includes(emp.staff_id));
    onToggleSelectAll(deptStaffIds, !allDeptSelected);
  };

  const toggleDepartmentExpand = (department) => {
    setCollapsedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(department)) {
        newSet.delete(department);
      } else {
        newSet.add(department);
      }
      return newSet;
    });
  };

  const getSelectedCountForDepartment = (department) => {
    const deptEmployees = groupedByDepartment.groups[department] || [];
    return deptEmployees.filter(emp => selectedEmployees.includes(emp.staff_id)).length;
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header — 24px gutter from panel edge (DS 8dp: px-6) */}
      <div className="px-6 py-3 border-b border-gray-200 space-y-3 flex-shrink-0">
        {/* Search - Pill Shape */}
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={setSearchTerm}
          placeholder="Search employees…"
        />
        
        {/* Search Result Count */}
        {debouncedSearchTerm.trim() && (
          <div className="text-xs text-gray-500">
            Found {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} matching "{debouncedSearchTerm}"
          </div>
        )}

        {/* Select All / Count — checkbox aligns with department/employee checkboxes */}
        <div className="flex items-center justify-between gap-3 text-sm">
          <button
            type="button"
            onClick={handleSelectAllClick}
            className="flex items-center gap-2.5 text-gray-600 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
          >
            {/* Spacer matches chevron column so checkbox lines up with row checkboxes */}
            <span className="w-4 flex-shrink-0" aria-hidden="true" />
            {allSelected ? (
              <CheckSquare className="w-4 h-4 text-red-600 flex-shrink-0" />
            ) : totalSelected > 0 ? (
              <MinusSquare className="w-4 h-4 text-red-600 flex-shrink-0" />
            ) : (
              <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )}
            <span>{allSelected ? 'Deselect all' : 'Select all'}</span>
          </button>
          <CountBadge
            count={totalSelected > 0 ? totalSelected : filteredEmployees.length}
            theme="admin"
            size="md"
            max={9999}
          />
        </div>
      </div>

      {/* Employee List - Grouped by Department */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="space-y-2 animate-pulse">
            {[1, 2, 3].map(deptIndex => (
              <div key={deptIndex} className="space-y-1">
                {/* Department Header Skeleton */}
                <div className="flex items-center gap-2.5 px-6 py-2.5 bg-gray-100 border-b border-gray-200 border-l-2 border-l-transparent">
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="w-6 h-6 bg-gray-200 rounded-full ml-auto"></div>
                </div>
                {/* Employee Rows Skeleton */}
                {[1, 2, 3].map(empIndex => (
                  <div key={empIndex} className="flex items-center gap-2.5 px-6 py-2.5 border-l-2 border-l-transparent">
                    <div className="w-4 h-4 flex-shrink-0"></div>
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    <div className="flex-1 space-y-1.5">
                      <div className="h-4 bg-gray-200 rounded w-40"></div>
                      <div className="h-3 bg-gray-200 rounded w-28"></div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500 px-6">
            <Users className="w-8 h-8 mb-2 text-gray-300" />
            <p className="text-sm text-center">
              {debouncedSearchTerm.trim() 
                ? `No employees match "${debouncedSearchTerm}"` 
                : 'No employees available'}
            </p>
          </div>
        ) : (
          <div>
            {groupedByDepartment.sortedDepts.map((department) => {
              const deptEmployees = groupedByDepartment.groups[department];
              const isExpanded = !collapsedDepartments.has(department);
              const selectedInDept = getSelectedCountForDepartment(department);

              return (
                <div key={department} className="border-b border-gray-100 last:border-b-0">
                  {/* Department Header */}
                  <DepartmentHeader
                    department={department}
                    employeeCount={deptEmployees.length}
                    selectedCount={selectedInDept}
                    isExpanded={isExpanded}
                    onToggleExpand={() => toggleDepartmentExpand(department)}
                    onSelectAll={() => handleDepartmentSelectAll(department)}
                  />

                  {/* Employee List for this Department */}
                  {isExpanded && (
                    <ul className="divide-y divide-gray-100">
                      {deptEmployees.map((employee) => {
                        const quality = getQualityInfo(employee.staff_id);
                        const selected = isSelected(employee.staff_id);
                        const previewing = isPreviewing(employee.staff_id);
                        const designation = employee.designation || 'No designation';

                        return (
                          <li
                            key={employee.staff_id}
                            onClick={(e) => handleRowClick(employee, e)}
                            className={`
                              px-6 py-2.5 cursor-pointer transition-colors border-l-2
                              ${previewing ? 'bg-red-50 border-l-red-600' : 'table-row border-l-transparent'}
                              ${selected && !previewing ? 'bg-red-50/30' : ''}
                            `}
                          >
                            <div className="flex items-start gap-2.5">
                              {/* Spacer = chevron column — keeps checkbox inset & aligned under dept checkbox */}
                              <span className="w-4 flex-shrink-0" aria-hidden="true" />

                              {/* Checkbox */}
                              <div className="checkbox-area pt-0.5 flex-shrink-0">
                                {selected ? (
                                  <CheckSquare className="w-4 h-4 text-red-600" />
                                ) : (
                                  <Square className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                )}
                              </div>

                              {/* Employee Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900 text-sm truncate">
                                    {employee.full_name}
                                  </span>
                                  <QualityIndicator
                                    score={quality.quality_score}
                                    missingCount={quality.missing_count}
                                  />
                                </div>
                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                  {designation} · {employee.staff_id}
                                </p>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer - Selected Count */}
      {totalSelected > 0 && (
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <p className="text-sm text-center text-gray-600">
            <span className="font-semibold text-red-600">{totalSelected}</span>
            {' '}employee{totalSelected !== 1 ? 's' : ''} selected for CV generation
          </p>
        </div>
      )}
    </div>
  );
};

export default EmployeeListPanel;
